<template>
  <div class="rcp-screen">
    <recipes-tools v-if="ctl.hasData"></recipes-tools>
    <recipes-state v-if="ctl.stateKind" :kind="ctl.stateKind"></recipes-state>
    <section v-else class="ui-card rd-card rcp-total-card">
      <div class="rcp-total-wrap">
        <table class="rcp-total-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" :aria-sort="ariaSort('name')">
                <button type="button" @click="ctl.sortBy('name')">{{ $t("grocery") }}<sort-mark :on="sortKey === 'name'" :dir="ctl.sort.dir"></sort-mark></button>
              </th>
              <th v-for="shift in shifts" :key="shift" scope="col" :aria-sort="ariaSort(`s${shift}`)">
                <button type="button" @click="ctl.sortBy(`s${shift}`)">
                  <report-shift-tag :shift="shift"></report-shift-tag>
                  <sort-mark :on="sortKey === `s${shift}`" :dir="ctl.sort.dir"></sort-mark>
                </button>
              </th>
              <th scope="col" :aria-sort="ariaSort('total')">
                <button type="button" @click="ctl.sortBy('total')">{{ $t("total") }}<sort-mark :on="sortKey === 'total'" :dir="ctl.sort.dir"></sort-mark></button>
              </th>
              <template v-if="ctl.price">
                <th scope="col">{{ $t("recipes_shell.unit_price") }}</th>
                <th scope="col" :aria-sort="ariaSort('cost')">
                  <button type="button" @click="ctl.sortBy('cost')">{{ $t("recipes_shell.cost") }}<sort-mark :on="sortKey === 'cost'" :dir="ctl.sort.dir"></sort-mark></button>
                </th>
              </template>
              <th scope="col" :aria-sort="ariaSort('meals')">
                <button type="button" @click="ctl.sortBy('meals')">{{ $t("recipes_shell.kpi.meals") }}<sort-mark :on="sortKey === 'meals'" :dir="ctl.sort.dir"></sort-mark></button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in ctl.totalsShown"
              :key="row.key"
              tabindex="0"
              role="button"
              :class="{ 'is-on': openKey === row.key }"
              :aria-label="$t('recipes_shell.split_by_meals', { name: toLatin(row.name) })"
              @click="open(row, $event)"
              @keydown.enter.prevent="open(row, $event)"
              @keydown.space.prevent="open(row, $event)"
            >
              <td class="is-index">{{ index + 1 }}</td>
              <td class="rcp-name"><recipe-hit :text="toLatin(row.name)" :query="query"></recipe-hit></td>
              <td v-for="(value, i) in row.shown" :key="shifts[i]" :class="{ 'is-zero': !value }">{{ value ? ctl.fq(value) : "—" }}</td>
              <td class="rcp-total">
                <b>{{ ctl.fq(row.total) }}</b>
                <span class="rcp-unit">&nbsp;{{ row.measure }}</span>
              </td>
              <template v-if="ctl.price">
                <td>{{ row.price == null ? "—" : ctl.fm(row.price) }}</td>
                <td>{{ row.cost == null ? "—" : ctl.fm(row.cost) }}</td>
              </template>
              <td>
                <span class="ui-chip ui-chip--pill">{{ ctl.pluralT("recipes_shell.meals_n", row.meals) }}</span>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="ctl.price">
            <tr>
              <td></td>
              <td class="rcp-name">{{ $t("recipes_shell.total_cost") }}</td>
              <td :colspan="shifts.length + 2"></td>
              <td>{{ totalCost }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import IngredientPanel from "../parts/IngredientPanel.vue";
import RecipeHit from "../parts/RecipeHit.vue";
import RecipesState from "../parts/RecipesState.vue";
import RecipesTools from "../parts/RecipesTools.vue";

/** Strelica za poredak u zaglavlju kolone (samo kod kolone po kojoj je sortirano). */
const SortMark = {
  functional: true,
  props: { on: Boolean, dir: Number },
  render(h, { props }) {
    return props.on ? h("v-icon", { props: { size: 14 } }, props.dir > 0 ? "mdi-arrow-up" : "mdi-arrow-down") : null;
  },
};

/**
 * "Zbirno": sve namirnice izabranih jela u jednoj tabeli koja se sortira po koloni. Klik na red otvara panel sa
 * raspodjelom te namirnice po jelima. Poredak količina važi unutar jedinice (kilogrami nisu veći od komada).
 */
export default {
  name: "RecipesTotal",
  components: { ReportShiftTag, RecipeHit, RecipesState, RecipesTools, SortMark },
  inject: { ctl: "recipesCtl", shell: { from: "reportShell", default: null } },
  computed: {
    shifts() {
      return [...this.ctl.shifts].sort();
    },
    sortKey() {
      return this.ctl.sort.key;
    },
    query() {
      return this.ctl.search.trim();
    },
    openKey() {
      const stack = this.shell ? this.shell.panelStack : [];
      const top = stack.length ? stack[stack.length - 1] : null;
      return top && top.key.startsWith("ing:") ? top.key.slice(4) : null;
    },
    totalCost() {
      const sum = this.ctl.totalsShown.reduce((total, row) => total + (row.cost || 0), 0);
      return [this.ctl.fm(sum), this.ctl.currency].filter(Boolean).join(" ");
    },
  },
  methods: {
    ariaSort(key) {
      if (this.sortKey !== key) {
        return null;
      }
      return this.ctl.sort.dir > 0 ? "ascending" : "descending";
    },
    open(row, event) {
      this.$emit("open-panel", {
        key: `ing:${row.key}`,
        title: this.toLatin(row.name),
        subtitle: `${this.ctl.fq(row.total)} ${row.measure} · ${this.ctl.pluralT("recipes_shell.meals_n", row.meals)}`,
        component: IngredientPanel,
        props: { rowKey: row.key },
        root: true,
        opener: event.currentTarget,
      });
    },
  },
};
</script>
